import {
  FormSectionStateArray,
  FormState,
  StateField,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { BunkerVm } from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { getFieldFromFormState, isSectionValid } from '../vm-builder-helpers';

const ISO_FUEL_GRADE_MAP: Record<string, string[]> = {
  hfo: ['RME180', 'RMG180', 'RMG380', 'RMG500', 'RMG700', 'RMK380', 'RMK500', 'RMK700'],
  lfo: ['RMA10', 'RMB30', 'RMD80'],
  mgo: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  mdo: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  b10lfo: ['RMA10', 'RMB30', 'RMD80'],
  b10mgo: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  biolfo: ['RMA10', 'RMB30', 'RMD80'],
  biomgo: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  ulsfo2020: [
    'RMA10',
    'RMB30',
    'RMD80',
    'RME180',
    'RMG180',
    'RMG380',
    'RMG500',
    'RMG700',
    'RMK380',
    'RMK500',
    'RMK700',
    'DMA',
    'DMX',
    'DFA',
    'DMZ',
    'DFZ',
    'DMB',
  ],
  ulslfo2020: ['RMA10', 'RMB30', 'RMD80'],
  ulsmdo2020: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  ulsmgo2020: ['DMA', 'DMX', 'DFA', 'DMZ', 'DFZ', 'DMB', 'DFB'],
  vlsfo2020: [
    'RMA10',
    'RMB30',
    'RMD80',
    'RME180',
    'RMG180',
    'RMG380',
    'RMG500',
    'RMG700',
    'RMK380',
    'RMK500',
    'RMK700',
    'DMA',
    'DMX',
    'DFA',
    'DMZ',
    'DFZ',
    'DMB',
  ],
  vlslfo2020: ['RMA10', 'RMB30', 'RMD80'],
  lpgp: ['LPG'],
  lpgb: ['LPG'],
  lng: ['LNG'],
  methanol: ['Methanol'],
  ethanol: ['Ethanol'],
  hvo: ['HVO'],
  fame: ['FAME'],
  biodiesel: ['Biodiesel'],
  bio_lng: ['Bio-LNG'],
  other: ['Other'],
} as const;

const getFieldFromNestedFormState = (
  formState: FormState,
  sectionId: string,
  nestedSectionId: string,
  index: number,
  fieldId: string
) => {
  const section = formState[sectionId];
  if (!section) return;

  const nestedSection = section[nestedSectionId] as FormSectionStateArray;
  if (!nestedSection) return;

  const entry = nestedSection[index];
  if (!entry) return;

  return entry[fieldId] as StateField<StateFieldType>;
};

export function buildBunkerVm(formState: FormState): BunkerVm {
  const isFuelOilBunkerChecked = () => {
    const field = getFieldFromFormState(formState, 'options', 'tookFuelOil');
    return (field && 'value' in field ? field.value : false) as boolean;
  };

  const isLubeOilBunkerChecked = () => {
    const field = getFieldFromFormState(formState, 'options', 'tookLubeOil');
    return (field && 'value' in field ? field.value : false) as boolean;
  };

  const isDebunkeredChecked = () => {
    const field = getFieldFromFormState(formState, 'options', 'debunkered');
    return (field && 'value' in field ? field.value : false) as boolean;
  };

  const getFuelGrade = (rowIndex: number) => {
    const field = getFieldFromNestedFormState(formState, 'fuelOilBunkerTable', 'bunkerRows', rowIndex, 'fuelType');
    return ((field && 'value' in field ? field.value : null) as SelectOption)?.code as string;
  };

  const getIsoFuelGrades = () => {
    return [
      ISO_FUEL_GRADE_MAP[getFuelGrade(0)],
      ISO_FUEL_GRADE_MAP[getFuelGrade(1)],
      ISO_FUEL_GRADE_MAP[getFuelGrade(2)],
    ];
  };

  const isFuelOilBunkerValid =
    !isFuelOilBunkerChecked() ||
    (isSectionValid(formState['fuelOilBunkers'], true) && isSectionValid(formState['fuelOilBunkerTable'], true));
  const isLubeOilBunkerValid = !isLubeOilBunkerChecked() || isSectionValid(formState['lubeOilBunkers'], true);
  const isDebunkerValid = !isDebunkeredChecked() || isSectionValid(formState['debunkered'], true);
  const isBunkerValid = isFuelOilBunkerValid && isLubeOilBunkerValid && isDebunkerValid;

  return {
    isFuelOilBunkerValid,
    isLubeOilBunkerValid,
    isDebunkerValid,
    isBunkerValid,
    showFuelOilBunkers: isFuelOilBunkerChecked(),
    showLubeOilBunkers: isLubeOilBunkerChecked(),
    showDebunkered: isDebunkeredChecked(),
    isoFuelGrades: getIsoFuelGrades(),
  };
}
